/**
 * Text-based PDF builders (jsPDF + jspdf-autotable).
 *
 * We generate three independent PDFs from the SAME reading object:
 *   • buildBasicPdf              — Basic-tier Kundali reading
 *   • buildPremiumAstroPdf       — Premium Vedic Astrology (Kundali + AI reading)
 *   • buildPremiumNumerologyPdf  — Premium Vedic Numerology
 *
 * Design choices
 * --------------
 * • Real, selectable, searchable text via `doc.text` — not rasterized DOM
 *   screenshots. The user can Ctrl-F, highlight & copy, and screen readers
 *   can announce every word.
 * • Page 1 of every PDF opens with a "Satish Numero World" gilded banner,
 *   followed by the traditional Ganesha invocation image (JPG, 64KB).
 * • Layout constants (margins, colours, fonts) live in `LAYOUT` at the top
 *   so future refactors are one-line changes.
 */
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toPng } from "html-to-image";
import i18n from "../i18n";
import ganeshaBanner from "../assets/ganesha-banner-pdf.jpg";
import snwLogo from "../assets/snw-logo.jpg";

/** Get a bound `t()` function for the reading's language. The PDF is
 *  generated after `i18n` initialisation, so `getFixedT(lang)` returns a
 *  language-locked translator. `inputs.lang` is set by the frontend when
 *  starting the reading and persisted on the reading document, so an old
 *  reading always exports in the language it was requested in — even if
 *  the user has since switched their site language. Falls back to 'en'. */
function makeT(lang) {
  const target = (lang || "en").toLowerCase();
  const supported = ["en", "hi", "te"];
  return i18n.getFixedT(supported.includes(target) ? target : "en", "translation", "pdf");
}

// Module-level translator ref. Each builder sets this at the top so all
// helper functions can pick it up without having to thread `t` through
// every function signature. PDF generation is single-flight per doc, so
// there's no race between concurrent builds.
let T = (k) => k;
function bindLang(lang) { T = makeT(lang); return T; }

// ---------- non-Latin font registration --------------------------------------
// jsPDF's built-in Helvetica has no Devanagari / Telugu glyphs — Hindi and
// Telugu text prints as random ASCII punctuation. We lazy-load the Noto Sans
// TTF for the target script the first time a non-English PDF is built,
// register it in the doc's virtual file-system, then swap the module-level
// `fonts.body` + `fonts.heading` to that family. Cached across the tab
// lifetime so subsequent downloads incur zero network cost.
const FONT_URLS = {
  hi: { name: "NotoSansDevanagari", url: "/fonts/NotoSansDevanagari-Regular.ttf" },
  te: { name: "NotoSansTelugu",     url: "/fonts/NotoSansTelugu-Regular.ttf" },
};
const FONT_CACHE = {}; // { hi: <base64 string>, te: <base64 string> }

async function loadFontB64(lang) {
  if (FONT_CACHE[lang]) return FONT_CACHE[lang];
  const spec = FONT_URLS[lang];
  if (!spec) return null;
  const res = await fetch(spec.url);
  if (!res.ok) return null;
  const buf = await res.arrayBuffer();
  // Convert to base64 without stack-blowing on large buffers.
  let bin = "";
  const bytes = new Uint8Array(buf);
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  const b64 = btoa(bin);
  FONT_CACHE[lang] = b64;
  return b64;
}

/** Register the Noto Sans font for the target language on the supplied
 *  jsPDF instance so subsequent `doc.setFont("NotoSansDevanagari"|"NotoSansTelugu", …)`
 *  calls resolve to real glyphs. Use `pickFontForText(text)` at each draw
 *  site to switch to the Noto font ONLY for strings that contain that
 *  script — Latin dynamic values (names, dates, planet names) keep
 *  helvetica because Noto's script subsets omit Latin glyphs entirely. */
async function activateScriptFontForLang(doc, lang) {
  if (!lang || lang === "en") return;
  const spec = FONT_URLS[lang];
  if (!spec) return;
  const b64 = await loadFontB64(lang);
  if (!b64) return;
  const filename = `${spec.name}-Regular.ttf`;
  doc.addFileToVFS(filename, b64);
  doc.addFont(filename, spec.name, "normal");
  doc.addFont(filename, spec.name, "bold");
  doc.addFont(filename, spec.name, "italic");
  // NOTE: we do NOT mutate LAYOUT.fonts here — Noto Sans Devanagari/Telugu
  // TTFs from Google's static hosting ship WITHOUT Latin glyphs, so making
  // them the default would blank out every Latin dynamic value (names,
  // dates, planet signs). Instead pickFontForText() switches per-string.
}

function restoreFonts() { /* nothing to restore now that we don't mutate LAYOUT */ }

/** Detect the dominant script of a piece of text and return the jsPDF font
 *  family that renders it correctly. Devanagari and Telugu strings pick up
 *  their respective Noto Sans; everything else stays on helvetica (which
 *  is guaranteed available and has full Latin coverage). */
function pickFontForText(text) {
  if (!text) return "helvetica";
  const s = String(text);
  if (/[\u0900-\u097F]/.test(s)) return "NotoSansDevanagari";
  if (/[\u0C00-\u0C7F]/.test(s)) return "NotoSansTelugu";
  return "helvetica";
}

/** Wrapper around doc.setFont that auto-picks the right family for the
 *  given text. Callers should invoke this immediately before any doc.text
 *  call that might render Hindi or Telugu. */
function setFontForText(doc, text, weight = "normal") {
  const family = pickFontForText(text);
  // Noto Sans Devanagari/Telugu were registered with normal/bold/italic
  // sub-styles that all point at the same TTF — so any weight is safe.
  doc.setFont(family, weight);
}

/** Shared jsPDF-autotable `didParseCell` hook — inspects each cell's raw
 *  text and forces the cell font family to whichever registered font
 *  actually contains the glyphs. Prevents Hindi/Telugu column values from
 *  rendering as random Latin punctuation. */
function tableFontHook(data) {
  const raw = data.cell.raw;
  const s = typeof raw === "string" ? raw : (raw != null ? String(raw) : "");
  data.cell.styles.font = pickFontForText(s);
}

/** Pick the language for the PDF. Priority:
 *  1. `inputs.lang`     — the language the reading was generated in
 *  2. `i18n.language`   — the user's current UI language
 *  3. "en"              — fallback
 *
 *  If the AI advice text was already generated in a specific language
 *  (stored on the reading), keep that. Otherwise honour what the user is
 *  browsing in — covers older readings pre-i18n and the "switch site
 *  language then re-download" flow. */
function pickPdfLang(inputs, reading) {
  const readingLang = inputs?.lang || reading?.inputs?.lang;
  if (readingLang && readingLang !== "en") return readingLang;
  const uiLang = (i18n.resolvedLanguage || i18n.language || "en").toLowerCase();
  return uiLang || "en";
}

// ---------- design tokens ---------------------------------------------------
const LAYOUT = {
  page:   { w: 210, h: 297 },        // A4 in mm
  margin: { x: 18, y: 20 },
  brand:  {
    ink:    "#5C3A09",               // deep brown for headings
    gold:   "#B8860B",               // accent
    saffron:"#D96600",               // rules
    body:   "#2A1A05",               // main text
    subtle: "#8B5E1A",               // captions
  },
  fonts:  { heading: "helvetica", body: "times" },
};

// ---------- primitives ------------------------------------------------------
/** Convert #RRGGBB to [r, g, b]. */
const hex = (h) => {
  const n = parseInt(h.slice(1), 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
};

/** jsPDF's built-in `align: "center"` measures text width WITHOUT accounting
 *  for `charSpace`, so any centred text with letter-spacing ends up visually
 *  shifted a little to the left. This helper recomputes the true rendered
 *  width (glyph width + inter-character spacing) and positions the text so
 *  it is truly centred on the page's vertical axis. All the `text-align:
 *  center` cover / section headings route through this helper. */
function drawCenteredText(doc, text, y, { charSpace = 0 } = {}) {
  const { page } = LAYOUT;
  // Devanagari/Telugu strings must render in the registered Noto font — the
  // default helvetica has no glyphs for these scripts. jsPDF preserves the
  // caller's font weight/size, only the family changes.
  const currentSize = doc.getFontSize();
  const currentState = doc.internal.getFont();
  const family = pickFontForText(text);
  if (family !== currentState.fontName) {
    doc.setFont(family, currentState.fontStyle || "normal");
    doc.setFontSize(currentSize);
  }
  // Devanagari/Telugu scripts already have natural inter-glyph rhythm — the
  // charSpace we tune for Latin caps looks jarring on Indic text, so damp it.
  const cs = family === "helvetica" ? charSpace : 0;
  const w = doc.getTextWidth(text) + Math.max(0, text.length - 1) * cs;
  doc.text(text, (page.w - w) / 2, y, cs ? { charSpace: cs } : undefined);
}

// ---------- helpers for cover metadata boxes --------------------------------

const RASHI_SANSKRIT = {
  "Aries": "Mesha", "Taurus": "Vrishabha", "Gemini": "Mithuna",
  "Cancer": "Karka", "Leo": "Simha", "Virgo": "Kanya",
  "Libra": "Tula", "Scorpio": "Vrishchika", "Sagittarius": "Dhanu",
  "Capricorn": "Makara", "Aquarius": "Kumbha", "Pisces": "Meena",
};

/** Ruler planet for each mulank-derived Numerology Mahadasha. Matches the
 *  Vedic mapping used on-screen in the Premium Numerology card and in
 *  `/app/backend/numerology.py` (VEDIC_PLANET_MAP). Backend's num_dasha.py
 *  currently doesn't echo the planet name back per Mahadasha row, so we
 *  derive it here from `m.number` (1..9). */
const NUMEROLOGY_RULERS = {
  1: "Sun (Surya)",
  2: "Moon (Chandra)",
  3: "Jupiter (Guru)",
  4: "Rahu (North Node)",
  5: "Mercury (Budha)",
  6: "Venus (Shukra)",
  7: "Ketu (South Node)",
  8: "Saturn (Shani)",
  9: "Mars (Mangal)",
};

/** Format a raw ISO-ish date-of-birth string into "5 February 1976". */
const formatDob = (dob) => {
  if (!dob) return "—";
  const d = new Date(dob);
  if (isNaN(d)) return dob;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

/** Draw a single metadata box (top label + big value + optional sanskrit).
 *  The label auto-shrinks (fontSize + charSpace) to guarantee it fits inside
 *  the box width — long labels like "BHAGYANK (DESTINY NUMBER)" would
 *  otherwise clip the right border. */
function drawMetaBox(doc, x, y, w, h, label, value, sub) {
  const { brand } = LAYOUT;
  doc.setDrawColor(...hex(brand.gold));
  doc.setLineWidth(0.4);
  doc.setFillColor(255, 251, 242);
  doc.rect(x, y, w, h, "FD");

  // Label — small caps, auto-fit inside the box with a comfortable 3 mm
  // inset from each vertical border. Long labels like "BHAGYANK (DESTINY
  // NUMBER)" would otherwise sit right on the border line.
  const labelText = (label || "").toUpperCase();
  setFontForText(doc, labelText, "bold");
  doc.setTextColor(...hex(brand.gold));
  const labelIsLatin = pickFontForText(labelText) === "helvetica";
  let labelSize = 7.5;
  let labelSpace = labelIsLatin ? 1.2 : 0;
  const fits = () => doc.getTextWidth(labelText) + Math.max(0, labelText.length - 1) * labelSpace <= w - 6;
  doc.setFontSize(labelSize);
  if (!fits()) { labelSpace = labelIsLatin ? 0.4 : 0; }
  if (!fits()) { labelSpace = 0; }
  while (!fits() && labelSize > 5.2) { labelSize -= 0.25; doc.setFontSize(labelSize); }
  doc.text(labelText, x + w / 2, y + 5, { align: "center", charSpace: labelSpace });

  // Value — main text (may be Latin or Indic depending on translation)
  const val = value || "—";
  setFontForText(doc, val, "bold");
  doc.setFontSize(11);
  doc.setTextColor(...hex(brand.ink));
  const wrapped = doc.splitTextToSize(val, w - 6);
  const valY = y + 5 + 6 + (wrapped.length === 1 ? 3 : 0);
  wrapped.slice(0, 2).forEach((ln, i) => {
    setFontForText(doc, ln, "bold");
    doc.text(ln, x + w / 2, valY + i * 4.6, { align: "center" });
  });

  // Sanskrit / sub caption
  if (sub) {
    setFontForText(doc, sub, "italic");
    doc.setFontSize(8);
    doc.setTextColor(...hex(brand.subtle));
    doc.text(sub, x + w / 2, y + h - 3, { align: "center" });
  }
}

/** Cover page for the Astrology (Basic + Premium) PDFs. Matches the sample
 *  reference PDF layout: brand + Ganesha + name + 3-box birth grid +
 *  Nakshatra summary + SNW watermark. */
function drawAstroCoverPage(doc, reading, inputs, reportTitle) {
  const { page, margin, brand, fonts } = LAYOUT;
  const src = inputs || reading.inputs || {};

  // 1) Brand band
  const bandY = margin.y - 4;
  doc.setDrawColor(...hex(brand.gold));
  doc.setLineWidth(0.4);
  doc.line(margin.x, bandY,     page.w - margin.x, bandY);
  doc.line(margin.x, bandY + 20, page.w - margin.x, bandY + 20);

  doc.setFont(fonts.heading, "bold");
  doc.setFontSize(22);
  doc.setTextColor(...hex(brand.ink));
  drawCenteredText(doc, T("brand").toUpperCase(), bandY + 11, { charSpace: 1.4 });
  doc.setFont(fonts.heading, "normal");
  doc.setFontSize(8);
  doc.setTextColor(...hex(brand.gold));
  drawCenteredText(doc, T("tagline"), bandY + 17.5, { charSpace: 2 });

  // 2) Ganesha invocation
  const gY = bandY + 26;
  const gH = 44;
  const gW = 44;
  doc.addImage(ganeshaBanner, "JPEG", (page.w - gW) / 2, gY, gW, gH, undefined, "FAST");

  doc.setFont(fonts.body, "italic");
  doc.setFontSize(10);
  doc.setTextColor(...hex(brand.ink));
  setFontForText(doc, T("invocation"), "italic");
  doc.text(T("invocation"), page.w / 2, gY + gH + 5, { align: "center" });
  doc.setFontSize(8);
  doc.setTextColor(...hex(brand.subtle));
  setFontForText(doc, T("invocation_sub"), "italic");
  doc.text(T("invocation_sub"), page.w / 2, gY + gH + 10, { align: "center" });

  // 3) Report title + subject name
  let y = gY + gH + 20;
  doc.setFont(fonts.heading, "bold");
  doc.setFontSize(11);
  doc.setTextColor(...hex(brand.gold));
  drawCenteredText(doc, (reportTitle || T("vedic_kundali_report")).toUpperCase(), y, { charSpace: 1.5 });

  y += 8;
  doc.setFont(fonts.heading, "bold");
  doc.setFontSize(20);
  doc.setTextColor(...hex(brand.ink));
  setFontForText(doc, src.full_name || reading.summary?.name || "—", "bold");
  doc.text(src.full_name || reading.summary?.name || "—", page.w / 2, y, { align: "center" });

  // Gold hairline under name
  doc.setDrawColor(...hex(brand.gold));
  doc.setLineWidth(0.3);
  doc.line(page.w / 2 - 40, y + 3, page.w / 2 + 40, y + 3);

  // 4) Three-box birth grid
  y += 12;
  const usable = page.w - margin.x * 2;
  const gap = 4;
  const boxW = (usable - gap * 2) / 3;
  const boxH = 26;

  const asc = reading.chart?.ascendant_english || reading.summary?.ascendant || reading.ascendant;
  const sun = reading.sun_sign || reading.summary?.sun_sign;
  const moon = reading.moon_sign || reading.summary?.moon_sign;

  drawMetaBox(doc, margin.x + (boxW + gap) * 0, y, boxW, boxH,
    T("date_of_birth"), formatDob(src.date_of_birth));
  drawMetaBox(doc, margin.x + (boxW + gap) * 1, y, boxW, boxH,
    T("time_of_birth"), src.time_of_birth || "—");
  drawMetaBox(doc, margin.x + (boxW + gap) * 2, y, boxW, boxH,
    T("place_of_birth"), src.place_of_birth || reading.summary?.pob || "—");

  y += boxH + gap;
  drawMetaBox(doc, margin.x + (boxW + gap) * 0, y, boxW, boxH,
    T("ascendant"), asc || "—", asc && RASHI_SANSKRIT[asc] ? RASHI_SANSKRIT[asc] : null);
  drawMetaBox(doc, margin.x + (boxW + gap) * 1, y, boxW, boxH,
    T("sun_sign"), sun || "—", sun && RASHI_SANSKRIT[sun] ? RASHI_SANSKRIT[sun] : null);
  drawMetaBox(doc, margin.x + (boxW + gap) * 2, y, boxW, boxH,
    T("moon_sign"), moon || "—", moon && RASHI_SANSKRIT[moon] ? RASHI_SANSKRIT[moon] : null);

  y += boxH + 10;

  // 5) Nakshatra summary block
  const nak = reading.chart?.nakshatra_report;
  if (nak && y < page.h - 65) {
    doc.setFont(fonts.heading, "bold");
    doc.setFontSize(9);
    doc.setTextColor(...hex(brand.gold));
    drawCenteredText(doc, T("nakshatra_report_star"), y, { charSpace: 1.4 });
    y += 6;

    // Nakshatra name — English only. jsPDF's built-in Helvetica has no
    // Devanagari glyphs, so we intentionally drop the Sanskrit unicode form
    // here to avoid garbled output. (A future enhancement can addFont() a
    // NotoSansDevanagari.ttf and render nak.sanskrit as a real sub-line.)
    doc.setFont(fonts.heading, "bold");
    doc.setFontSize(16);
    doc.setTextColor(...hex(brand.ink));
    setFontForText(doc, nak.name || "—", "bold");
    doc.text(nak.name || "—", page.w / 2, y, { align: "center" });
    y += 6;

    // Pada + range — measure & auto-wrap to guarantee no overflow.
    doc.setFont(fonts.body, "italic");
    doc.setFontSize(9);
    doc.setTextColor(...hex(brand.saffron));
    const padaLine = `${T("pada")} ${nak.pada ?? "—"}${nak.range ? "   ·   " + nak.range : ""}`;
    const padaLines = doc.splitTextToSize(padaLine, usable - 20);
    padaLines.slice(0, 2).forEach((ln, i) => {
      setFontForText(doc, ln, "italic");
        doc.text(ln, page.w / 2, y + i * 4.5, { align: "center" });
    });
    y += Math.min(2, padaLines.length) * 4.5 + 1;

    // Description
    if (nak.description) {
      doc.setFont(fonts.body, "italic");
      doc.setFontSize(9);
      doc.setTextColor(...hex(brand.body));
      const wrapped = doc.splitTextToSize(nak.description, usable - 20);
      wrapped.slice(0, 2).forEach((ln, i) => {
        doc.text(ln, page.w / 2, y + i * 4.5, { align: "center" });
      });
      y += Math.min(2, wrapped.length) * 4.5 + 2;
    }

    // Small attribute strip: Deity · Gana · Symbol · Quality · Ruler
    const attrs = [
      ["Deity",   nak.deity, T("deity")],
      ["Gana",    nak.gana,  T("gana")],
      ["Symbol",  nak.symbol, T("symbol")],
      ["Quality", nak.quality, T("quality")],
      ["Ruler",   nak.ruler,   T("ruler")],
    ].filter(([, v]) => v);
    if (attrs.length) {
      y += 2;
      const stripW = (usable - 10) / attrs.length;
      attrs.forEach(([, val, tlabel], i) => {
        const cx = margin.x + 5 + stripW * i + stripW / 2;
        setFontForText(doc, tlabel.toUpperCase(), "bold");
        doc.setFontSize(6.5);
        doc.setTextColor(...hex(brand.gold));
        const isLat = pickFontForText(tlabel) === "helvetica";
        doc.text(tlabel.toUpperCase(), cx, y, { align: "center", charSpace: isLat ? 1 : 0 });
        setFontForText(doc, String(val), "normal");
        doc.setFontSize(8);
        doc.setTextColor(...hex(brand.body));
        doc.text(String(val), cx, y + 4, { align: "center" });
      });
    }
  }
}

/** Cover page for the Numerology PDF. Same aesthetic as astrology cover but
 *  the three-box grid + summary swap in numerology-specific numbers. */
function drawNumerologyCoverPage(doc, reading, inputs) {
  const { page, margin, brand, fonts } = LAYOUT;
  const src = inputs || reading.inputs || {};
  const num = reading.chart?.numerology;

  // Brand band (same as astrology)
  const bandY = margin.y - 4;
  doc.setDrawColor(...hex(brand.gold));
  doc.setLineWidth(0.4);
  doc.line(margin.x, bandY,     page.w - margin.x, bandY);
  doc.line(margin.x, bandY + 20, page.w - margin.x, bandY + 20);
  doc.setFont(fonts.heading, "bold");
  doc.setFontSize(22);
  doc.setTextColor(...hex(brand.ink));
  drawCenteredText(doc, T("brand").toUpperCase(), bandY + 11, { charSpace: 1.4 });
  doc.setFont(fonts.heading, "normal");
  doc.setFontSize(8);
  doc.setTextColor(...hex(brand.gold));
  drawCenteredText(doc, T("tagline"), bandY + 17.5, { charSpace: 2 });

  // Ganesha
  const gY = bandY + 26;
  const gH = 44, gW = 44;
  doc.addImage(ganeshaBanner, "JPEG", (page.w - gW) / 2, gY, gW, gH, undefined, "FAST");
  doc.setFont(fonts.body, "italic");
  doc.setFontSize(10);
  doc.setTextColor(...hex(brand.ink));
  doc.text(T("invocation"), page.w / 2, gY + gH + 5, { align: "center" });
  doc.setFontSize(8);
  doc.setTextColor(...hex(brand.subtle));
  doc.text(T("invocation_sub"), page.w / 2, gY + gH + 10, { align: "center" });

  // Report title + name
  let y = gY + gH + 20;
  doc.setFont(fonts.heading, "bold");
  doc.setFontSize(11);
  doc.setTextColor(...hex(brand.gold));
  drawCenteredText(doc, T("vedic_numerology_report"), y, { charSpace: 1.5 });
  y += 8;
  doc.setFont(fonts.heading, "bold");
  doc.setFontSize(20);
  doc.setTextColor(...hex(brand.ink));
  doc.text(src.full_name || reading.summary?.name || "—", page.w / 2, y, { align: "center" });
  doc.setDrawColor(...hex(brand.gold));
  doc.setLineWidth(0.3);
  doc.line(page.w / 2 - 40, y + 3, page.w / 2 + 40, y + 3);

  // Three-box birth grid (row 1)
  y += 12;
  const usable = page.w - margin.x * 2;
  const gap = 4;
  const boxW = (usable - gap * 2) / 3;
  const boxH = 26;
  drawMetaBox(doc, margin.x + (boxW + gap) * 0, y, boxW, boxH, T("date_of_birth"), formatDob(src.date_of_birth));
  drawMetaBox(doc, margin.x + (boxW + gap) * 1, y, boxW, boxH, T("time_of_birth"), src.time_of_birth || "—");
  drawMetaBox(doc, margin.x + (boxW + gap) * 2, y, boxW, boxH, T("place_of_birth"), src.place_of_birth || reading.summary?.pob || "—");

  // Row 2 — core numerology numbers
  y += boxH + gap;
  const numRow = (k, fallbackLabel) => {
    const e = num?.[k];
    return {
      label: e?.label || fallbackLabel,
      value: e ? `${e.number}  ·  ${e.planet_english || e.planet || ""}` : "—",
      sub: e?.traits ? e.traits.split(",")[0] : null,
    };
  };
  const mul = numRow("mulank",   "Mulank");
  const bha = numRow("bhagyank", "Bhagyank");
  const naa = numRow("naamank",  "Naamank");
  drawMetaBox(doc, margin.x + (boxW + gap) * 0, y, boxW, boxH, mul.label, mul.value, mul.sub);
  drawMetaBox(doc, margin.x + (boxW + gap) * 1, y, boxW, boxH, bha.label, bha.value, bha.sub);
  drawMetaBox(doc, margin.x + (boxW + gap) * 2, y, boxW, boxH, naa.label, naa.value, naa.sub);

  // Blessing footer strip
  y += boxH + 12;
  y = drawFittedTitle(doc, T("num_journey_tagline"), y, { size: 9, color: brand.gold, maxCharSpace: 1.2 });
  doc.setFont(fonts.body, "italic");
  doc.setFontSize(9.5);
  doc.setTextColor(...hex(brand.body));
  setFontForText(doc, T("num_blessing"), "italic");
  doc.text(T("num_blessing"), page.w / 2, y, { align: "center" });
}

/** Render a Markdown reading section. Handles all supported languages —
 *  the caller has already registered the correct Noto Sans font via
 *  `activateScriptFontForLang` so drawMarkdown's Latin/Devanagari/Telugu
 *  glyphs all render correctly. The `testId` and `lang` params are kept for
 *  API compatibility with earlier snapshot-based fallbacks. */
async function drawReadingBody(doc, markdown, y /* , testId, lang */) {
  drawMarkdown(doc, markdown, y);
}

function drawSectionHeading(doc, text, y) {
  const { margin, brand } = LAYOUT;
  setFontForText(doc, text, "bold");
  doc.setFontSize(14);
  doc.setTextColor(...hex(brand.ink));
  doc.text(text, margin.x, y);
  doc.setDrawColor(...hex(brand.saffron));
  doc.setLineWidth(0.35);
  doc.line(margin.x, y + 1.5, margin.x + 45, y + 1.5);
  return y + 8;
}

/** Wrap and draw a paragraph. Returns the y position after the paragraph. */
function drawParagraph(doc, text, y, opts = {}) {
  const { margin, page, brand, fonts } = LAYOUT;
  const size    = opts.size ?? 10;
  const color   = opts.color ?? brand.body;
  const font    = opts.font ?? fonts.body;
  const style   = opts.style ?? "normal";
  const leading = opts.leading ?? size * 0.55;
  const maxW    = page.w - margin.x * 2;

  doc.setFont(font, style);
  doc.setFontSize(size);
  doc.setTextColor(...hex(color));

  const lines = doc.splitTextToSize(text || "", maxW);
  for (const line of lines) {
    if (y > page.h - margin.y - 5) { doc.addPage(); y = margin.y; }
    setFontForText(doc, line, style);
    doc.text(line, margin.x, y);
    y += leading;
  }
  return y + 2;
}

/** Draw a center-aligned page heading that is guaranteed to fit within the
 *  usable page width by shrinking either the char-spacing or the font size.
 *  Prevents "RAVI KUM" style right-edge clipping we hit on long subject
 *  names ("DETAILED VEDIC KUNDALI READING FOR <NAME>"). */
function drawFittedTitle(doc, text, y, { size = 14, weight = "bold", color, maxCharSpace = 1.1 } = {}) {
  const { page, margin, brand } = LAYOUT;
  const maxW = page.w - margin.x * 2;
  setFontForText(doc, text, weight);
  doc.setFontSize(size);
  doc.setTextColor(...hex(color || brand.ink));

  // charSpace only makes sense for Latin — Indic scripts already have
  // natural inter-glyph spacing that gets ugly if we spread letters.
  const isLatin = pickFontForText(text) === "helvetica";
  let charSpace = isLatin ? maxCharSpace : 0;
  const measure = () => doc.getTextWidth(text) + (text.length - 1) * charSpace;
  if (isLatin && measure() > maxW) { charSpace = 0.3; }
  if (measure() > maxW) {
    charSpace = 0;
    let s = size;
    while (s > 9 && doc.getTextWidth(text) > maxW) {
      s -= 0.5;
      doc.setFontSize(s);
    }
  }
  const trueWidth = doc.getTextWidth(text) + Math.max(0, text.length - 1) * charSpace;
  doc.text(text, (page.w - trueWidth) / 2, y, charSpace ? { charSpace } : undefined);
  return y + Math.max(6, size * 0.55);
}

/** Convert Claude's Markdown reading into styled PDF blocks. Supports
 *  #/##/### headings, blank-line paragraph breaks and inline **bold**. */
function drawMarkdown(doc, md, y) {
  const { margin, page, brand } = LAYOUT;
  if (!md) return y;

  const blocks = md.replace(/\r\n/g, "\n").split(/\n{2,}/);
  for (const raw of blocks) {
    const block = raw.trim();
    if (!block) continue;

    if (y > page.h - margin.y - 15) { doc.addPage(); y = margin.y; }

    // Headings — order matters: ### before ## before # so the more-specific
    // pattern wins. Previously we skipped H1 entirely, which leaked a literal
    // "# " into the body when Claude opens the reading with a top-level title.
    const h3 = block.match(/^###\s+(.+)/);
    const h2 = block.match(/^##\s+(.+)/);
    const h1 = block.match(/^#\s+(.+)/);
    if (h3) {
      setFontForText(doc, h3[1], "bold");
      doc.setFontSize(11);
      doc.setTextColor(...hex(brand.gold));
      doc.text(h3[1], margin.x, y);
      y += 6;
      continue;
    }
    if (h2) {
      y = drawSectionHeading(doc, h2[1], y + 4);
      continue;
    }
    if (h1) {
      setFontForText(doc, h1[1], "bold");
      doc.setFontSize(12);
      doc.setTextColor(...hex(brand.saffron));
      const wrapped = doc.splitTextToSize(h1[1], page.w - margin.x * 2);
      wrapped.forEach((ln, i) => {
        setFontForText(doc, ln, "bold");
        doc.text(ln, LAYOUT.page.w / 2, y + i * 5.5, { align: "center" });
      });
      y += wrapped.length * 5.5 + 3;
      continue;
    }

    // Paragraph — flatten inline **bold** (jsPDF has no mixed-style runs in
    // one draw call, so we render the whole paragraph in normal weight and
    // strip the asterisks. Bold sentences will still read fine, they just
    // don't visually bold — an acceptable tradeoff for text-based PDFs).
    const clean = block.replace(/\*\*/g, "").replace(/\*/g, "");
    y = drawParagraph(doc, clean, y, { size: 10.5, leading: 5.8 });
  }
  return y;
}

/** Standard page footer — big SNW watermark background stamp + brand + page
 *  number. Applied to every page at build time (call at the end of the
 *  builder, after all pages have been added, so total-page-count is correct).
 *  Page 1 (cover) gets the footer text but skips the watermark so the brand
 *  cover isn't overshadowed. Watermark size and opacity are picked so the
 *  logo reads clearly as a background stamp but doesn't drown surrounding
 *  copy (tables and reading text render on top and remain fully legible). */
function drawFooter(doc) {
  const { page, margin, brand } = LAYOUT;
  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    // Bold background SNW logo watermark — content pages only (skip cover).
    if (i > 1) {
      const wmSize = 90;                              // 60 → 90 mm (1.5×) per user request
      const wmX = (page.w - wmSize) / 2;              // horizontally centred
      const wmY = page.h - wmSize - 22;               // above the footer text
      try {
        doc.saveGraphicsState();
        // Higher opacity (0.22 vs 0.14) so the mark reads as bolder, but still
        // low enough that overlaid tables / reading text remain fully legible.
        doc.setGState(new doc.GState({ opacity: 0.22 }));
        doc.addImage(snwLogo, "JPEG", wmX, wmY, wmSize, wmSize, undefined, "FAST");
        doc.restoreGraphicsState();
      } catch {
        doc.addImage(snwLogo, "JPEG", wmX, wmY, wmSize, wmSize, undefined, "FAST");
      }
    }
    doc.setFont(LAYOUT.fonts.body, "italic");
    doc.setFontSize(8);
    doc.setTextColor(...hex(brand.subtle));
    setFontForText(doc, T("brand"), "italic");
    doc.text(T("brand"), margin.x, page.h - 8);
    setFontForText(doc, T("page_of", { i, n: total }), "italic");
    doc.text(T("page_of", { i, n: total }), page.w - margin.x, page.h - 8, { align: "right" });
  }
}

// ---------- shared report sections ------------------------------------------

function drawPlanetaryPositions(doc, r, y) {
  const planets = r.chart?.planets;
  if (!planets) return y;
  y = drawSectionHeading(doc, T("planetary_positions"), y + 4);
  const list = Array.isArray(planets) ? planets : Object.values(planets);
  // Localised state labels — the backend emits states in English
  // ("Retrograde", "Vargottam", "Exalted", "Debilitated"); map them onto the
  // corresponding pdf.state_* i18n keys so the column reads natively in the
  // reader's language.
  const STATE_MAP = {
    "Direct": T("state_direct"),
    "Retrograde": T("state_retrograde"),
    "Vargottam": T("state_vargottam"),
    "Exalted": T("state_exalted"),
    "Debilitated": T("state_debilitated"),
  };
  const localiseState = (s) => STATE_MAP[s] || s;
  const rows = list.map((p) => {
    const stateBits = [];
    if (Array.isArray(p.states) && p.states.length) {
      stateBits.push(...p.states.map(localiseState));
    } else if (p.retrograde) {
      stateBits.push(T("state_retrograde"));
    }
    const state = stateBits.length ? stateBits.join(", ") : T("state_direct");
    return [
      p.name || p.code || "—",
      p.rashi_english || p.rashi || "—",
      p.degree != null
        ? (typeof p.degree === "number" ? p.degree.toFixed(2) : p.degree) + "°"
        : (p.deg_in_sign != null ? p.deg_in_sign.toFixed(2) + "°" : "—"),
      String(p.house ?? "—"),
      p.navamsha_sign_english || p.navamsha_sign || "—",
      state,
    ];
  });
  autoTable(doc, {
    startY: y, margin: { left: LAYOUT.margin.x, right: LAYOUT.margin.x },
    head: [[T("col_graha"), T("col_rashi"), T("col_degree"), T("col_house"), T("col_navamsha"), T("col_states")]],
    body: rows, theme: "grid",
    styles: { font: LAYOUT.fonts.body, fontSize: 9, cellPadding: 1.8, textColor: hex(LAYOUT.brand.body) },
    headStyles: { fillColor: hex(LAYOUT.brand.ink), textColor: [255, 255, 255], font: LAYOUT.fonts.heading, fontStyle: "bold", fontSize: 9, halign: "center" },
    alternateRowStyles: { fillColor: [253, 250, 240] },
    columnStyles: {
      0: { fontStyle: "bold" },
      2: { halign: "right" },
      3: { halign: "center" },
      5: { textColor: hex(LAYOUT.brand.saffron), fontStyle: "italic" },
    },
    didParseCell: tableFontHook,
  });
  return doc.lastAutoTable.finalY + 4;
}

function drawVimshottariMahadasha(doc, r, y) {
  const md = r.chart?.dasha?.mahadashas;
  if (!md?.length) return y;
  y = drawSectionHeading(doc, T("vimshottari_title"), y + 4);

  const cur = r.chart?.dasha?.current;
  const curLord   = cur?.mahadasha;
  const curAntar  = cur?.antardasha;
  const curPratya = cur?.pratyantardasha;
  y = drawParagraph(doc, T("vimshottari_intro"), y, { size: 9.5, leading: 5, color: LAYOUT.brand.body });
  if (curLord) {
    doc.setFont(LAYOUT.fonts.heading, "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(...hex(LAYOUT.brand.saffron));
    const chain = [curLord, curAntar, curPratya].filter(Boolean).join(" — ");
    setFontForText(doc, `${T("currently_running")}:  ${chain}`, "bold");
    doc.text(`${T("currently_running")}:  ${chain}`, LAYOUT.margin.x, y);
    y += 6;
  }

  const rows = md.map((m) => [
    m.lord + (m.lord === curLord ? `   ${T("current_marker")}` : ""),
    new Date(m.start).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
    new Date(m.end).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
    String(m.years),
  ]);
  autoTable(doc, {
    startY: y, margin: { left: LAYOUT.margin.x, right: LAYOUT.margin.x },
    head: [[T("col_mahadasha_lord"), T("col_starts"), T("col_ends"), T("col_years")]],
    body: rows, theme: "striped",
    styles: { font: LAYOUT.fonts.body, fontSize: 10, cellPadding: 2, textColor: hex(LAYOUT.brand.body) },
    headStyles: { fillColor: hex(LAYOUT.brand.ink), textColor: [255, 255, 255], fontStyle: "bold", halign: "center" },
    alternateRowStyles: { fillColor: [253, 250, 240] },
    columnStyles: { 0: { fontStyle: "bold" }, 3: { halign: "right" } },
    didParseCell: tableFontHook,
  });
  return doc.lastAutoTable.finalY + 4;
}

function drawNumerologyCore(doc, num, y) {
  if (!num) return y;
  y = drawSectionHeading(doc, T("numerology_overview"), y + 4);
  const rows = ["mulank", "bhagyank", "naamank"]
    .filter((k) => num[k]?.number)
    .map((k) => {
      const e = num[k];
      const planet = e.planet_english
        ? (e.planet && !e.planet_english.toLowerCase().includes(e.planet.toLowerCase())
            ? `${e.planet} (${e.planet_english})`
            : e.planet_english)
        : (e.planet || e.name || "—");
      const essence = e.traits || e.essence || "";
      return [e.label, String(e.number), planet, essence];
    });
  autoTable(doc, {
    startY: y, margin: { left: LAYOUT.margin.x, right: LAYOUT.margin.x },
    head: [["", T("col_number"), T("col_planet"), T("col_essence")]],
    body: rows, theme: "grid",
    styles: { font: LAYOUT.fonts.body, fontSize: 9.5, cellPadding: 2, textColor: hex(LAYOUT.brand.body), overflow: "linebreak" },
    headStyles: { fillColor: hex(LAYOUT.brand.ink), textColor: [255, 255, 255], fontStyle: "bold" },
    columnStyles: { 0: { cellWidth: 45 }, 1: { cellWidth: 12, halign: "center" }, 2: { cellWidth: 30 }, 3: { cellWidth: "auto" } },
    didParseCell: tableFontHook,
  });
  return doc.lastAutoTable.finalY + 4;
}

function drawLoShu(doc, lo, y) {
  if (!lo?.grid) return y;
  y = drawSectionHeading(doc, "Lo Shu Grid  ·  Jeevan Ank Yantra", y + 4);
  const rows = lo.grid.map((row) => row.map((c) => (c.count > 0 ? String(c.digit).repeat(c.count) : "")));
  autoTable(doc, {
    startY: y, margin: { left: LAYOUT.margin.x + 30, right: LAYOUT.margin.x + 30 },
    body: rows, theme: "grid",
    styles: { font: LAYOUT.fonts.heading, fontSize: 20, fontStyle: "bold", cellPadding: 8, halign: "center", valign: "middle", textColor: hex(LAYOUT.brand.saffron), lineColor: hex(LAYOUT.brand.subtle), lineWidth: 0.4, minCellHeight: 25 },
    didParseCell: tableFontHook,
  });
  return doc.lastAutoTable.finalY + 4;
}

function drawVedicPlanetChart(doc, vc, y) {
  if (!vc?.grid) return y;
  y = drawSectionHeading(doc, "Vedic Planetary Chart  ·  Grahas", y + 4);
  // Match on-screen + Lo Shu style: repeat the digit by its count when
  // present (e.g. "111" for count=3), single faded digit when missing.
  const rows = vc.grid.map((row) =>
    row.map((c) => {
      const digitStr = c.count > 0 ? String(c.digit).repeat(c.count) : String(c.digit);
      return `${digitStr}\n${c.graha}${c.count > 1 ? `  ×${c.count}` : ""}`;
    })
  );
  autoTable(doc, {
    startY: y, margin: { left: LAYOUT.margin.x + 30, right: LAYOUT.margin.x + 30 },
    body: rows, theme: "grid",
    styles: { font: LAYOUT.fonts.body, fontSize: 10, cellPadding: 4, halign: "center", valign: "middle", textColor: hex(LAYOUT.brand.body), lineColor: hex(LAYOUT.brand.subtle), lineWidth: 0.4, minCellHeight: 20 },
    didParseCell: (data) => {
      tableFontHook(data);
      // Fade missing-digit cells (count=0) like the on-screen chart.
      const rowIdx = data.row.index;
      const colIdx = data.column.index;
      const cell = vc.grid[rowIdx]?.[colIdx];
      if (cell && cell.count === 0) {
        data.cell.styles.textColor = hex(LAYOUT.brand.subtle);
        data.cell.styles.fillColor = [253, 251, 247];
      } else if (cell) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [250, 243, 220];
      }
    },
  });
  y = doc.lastAutoTable.finalY + 4;
  if (vc.dominant?.length) {
    doc.setFont(LAYOUT.fonts.body, "italic");
    doc.setFontSize(9.5);
    doc.setTextColor(...hex(LAYOUT.brand.subtle));
    const dom = vc.dominant.map((n) => {
      const c = vc.grid.flat().find((x) => x.digit === n);
      return c ? `${c.graha} (${c.english}) ×${c.count}` : "";
    }).filter(Boolean).join(" · ");
    doc.text(`Dominant grahas: ${dom}`, LAYOUT.margin.x, y);
    y += 6;
  }
  return y;
}

// ---------- Kundali charts (North Indian diamond) --------------------------
/** Draw a single North-Indian diamond Kundali as vector primitives at
 *  (x, y) with the given `size` (mm). Mirrors the layout of
 *  `/app/frontend/src/components/KundaliChart.jsx` (asc-marker, rashi number
 *  at the top of each cell, planet codes below). Titles above chart are
 *  drawn separately by the caller for tighter layout control. */
function drawKundaliDiagram(doc, chart, x, y, size) {
  const { brand, fonts } = LAYOUT;
  if (!chart?.house_signs || !chart?.houses) return;
  const s = size;

  // Outer square + diamond (midpoint quad) + full diagonals
  doc.setDrawColor(...hex(brand.gold));
  doc.setLineWidth(0.35);
  doc.rect(x, y, s, s);
  doc.line(x + s/2, y,       x + s,   y + s/2);
  doc.line(x + s,   y + s/2, x + s/2, y + s);
  doc.line(x + s/2, y + s,   x,       y + s/2);
  doc.line(x,       y + s/2, x + s/2, y);
  doc.line(x,       y,       x + s,   y + s);
  doc.line(x + s,   y,       x,       y + s);

  // North-Indian house centers (fractions of `s`, matches KundaliChart.jsx).
  const centers = {
    1:  [0.50, 0.25], 2:  [0.18, 0.12], 3:  [0.12, 0.22], 4:  [0.25, 0.50],
    5:  [0.12, 0.78], 6:  [0.18, 0.88], 7:  [0.50, 0.75], 8:  [0.82, 0.88],
    9:  [0.88, 0.78], 10: [0.75, 0.50], 11: [0.88, 0.22], 12: [0.82, 0.12],
  };

  // Font sizes scale gently with chart size so 80 mm and 110 mm both read well.
  // At 76 mm (Chandra / Navamsha) the small triangular cells only comfortably
  // fit ~3 two-letter codes on one line — shrink the planet font extra hard
  // there so a rare 4+ planet cell can't overflow into a neighbour.
  const rashiFs  = Math.max(7, Math.min(11, size * 0.10));
  const basePlanetFs = Math.max(5.5, Math.min(9, size * 0.075));

  for (let h = 1; h <= 12; h++) {
    const cx = x + centers[h][0] * s;
    const cy = y + centers[h][1] * s;
    const signIdx = chart.house_signs[String(h)];
    const planets = chart.houses[String(h)] || [];

    // Rashi number (1..12)
    doc.setFont(fonts.heading, "bold");
    doc.setFontSize(rashiFs);
    doc.setTextColor(...hex("#6B4308"));
    doc.text(String(((signIdx ?? 0) % 12) + 1), cx, cy - size * 0.015, { align: "center" });

    // Planet codes — wrap to 2 lines if more than 3 in the same house, and
    // shrink progressively so even a rare 4-planet cell fits inside the
    // triangular cell on the compact (76 mm) Chandra / Navamsha diagrams.
    if (planets.length) {
      const shrink = planets.length <= 2 ? 0
                   : planets.length === 3 ? 0.6
                   : planets.length === 4 ? 1.2
                   : 1.6;
      const planetFs = Math.max(4.5, basePlanetFs - shrink);
      doc.setFont(fonts.body, "normal");
      doc.setFontSize(planetFs);
      doc.setTextColor(...hex(brand.body));
      const lineY = cy + size * 0.045;
      if (planets.length <= 3) {
        doc.text(planets.join(" "), cx, lineY, { align: "center" });
      } else {
        const mid = Math.ceil(planets.length / 2);
        doc.text(planets.slice(0, mid).join(" "), cx, lineY,                { align: "center" });
        doc.text(planets.slice(mid).join(" "),    cx, lineY + size * 0.048, { align: "center" });
      }
    }
  }

  // Ascendant tag — small saffron pill above house 1
  const [ax, ay] = [x + centers[1][0] * s, y + centers[1][1] * s];
  doc.setDrawColor(...hex("#FF9933"));
  doc.setLineWidth(0.3);
  doc.setFillColor(255, 240, 220);
  doc.circle(ax, ay - size * 0.11, size * 0.032, "FD");
  doc.setFont(fonts.heading, "bold");
  doc.setFontSize(Math.max(5.5, size * 0.055));
  doc.setTextColor(...hex("#9A3E00"));
  doc.text("Asc", ax, ay - size * 0.098, { align: "center" });
}

// ---------- snapshot helpers (hybrid text + on-screen visuals) --------------
/** Snapshot a DOM element by data-testid into a PNG data-url plus its natural
 *  pixel dimensions. Returns null if the element isn't in the DOM or
 *  html-to-image throws (e.g. tainted canvas). Callers must handle null and
 *  fall back to vector-drawn primitives. */
async function snapshotByTestId(testId, options = {}) {
  if (typeof document === "undefined") return null;
  const el = document.querySelector(`[data-testid="${testId}"]`);
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return null;
  try {
    const dataUrl = await toPng(el, {
      pixelRatio: 2,
      backgroundColor: "#FDFBF7",
      cacheBust: true,
      // Skip any element already marked "no-print" (e.g. the "Click to expand"
      // hover hint on the kundali cards) so the PDF snapshot matches the
      // resting print state, not the interactive on-screen state.
      filter: (node) => {
        if (node.classList && node.classList.contains("no-print")) return false;
        return true;
      },
      // Neutralise the hover-lift transform + focus ring so the capture
      // looks like the resting state, not the mid-interaction state.
      style: { transform: "none", boxShadow: "none" },
      ...options,
    });
    return { dataUrl, w: rect.width, h: rect.height };
  } catch (e) {
    console.warn(`[pdfBuilders] snapshot failed for [data-testid="${testId}"]`, e);
    return null;
  }
}

/** Draw a captured PNG snapshot into the PDF, scaled to fit inside
 *  (maxW × maxH) preserving aspect ratio and horizontally centred inside the
 *  bounding box. Returns the actual rendered {w, h}. */
function drawSnapshot(doc, snap, x, y, maxW, maxH) {
  if (!snap) return { w: 0, h: 0 };
  const ratio = snap.h / snap.w;
  let w = maxW;
  let h = w * ratio;
  if (h > maxH) { h = maxH; w = h / ratio; }
  const cx = x + (maxW - w) / 2;
  doc.addImage(snap.dataUrl, "PNG", cx, y, w, h, undefined, "FAST");
  return { w, h };
}

/** Snapshot-driven Kundali charts. In the reference reference PDF the
 *  D1 Lagna chart sits ABOVE the Planetary Positions table on the same page
 *  (page 2), while Chandra Rashi and Navamsha D9 share page 3. We build the
 *  pages accordingly here — the caller supplies the target page layout via
 *  the `layout` option: "d1" (just D1, medium size), "chandra+navamsha"
 *  (both stacked), or "all" (legacy behaviour). Falls back to vector diamonds
 *  if the DOM elements are missing.  */
async function drawKundaliChartsFromScreen(doc, reading, testIds, layout = "all") {
  const { page, margin, brand, fonts } = LAYOUT;
  const [d1Id, chandraId, navamshaId] = testIds;

  const wantD1       = layout === "d1"  || layout === "all";
  const wantChandra  = layout === "chandra+navamsha" || layout === "all";
  const wantNavamsha = layout === "chandra+navamsha" || layout === "all";

  const [d1Snap, chSnap, navSnap] = await Promise.all([
    wantD1       ? snapshotByTestId(d1Id)       : Promise.resolve(null),
    wantChandra  ? snapshotByTestId(chandraId)  : Promise.resolve(null),
    wantNavamsha ? snapshotByTestId(navamshaId) : Promise.resolve(null),
  ]);

  // If NOTHING captured (e.g. running from ReadingDetail where the on-screen
  // chart uses a different id), fall back to the pure-vector rendering — but
  // only for the "all" layout. For the reference-PDF layout ("d1" or
  // "chandra+navamsha") the caller already advanced to the target page, so
  // silently return null and let the caller handle empty space.
  if (!d1Snap && !chSnap && !navSnap) {
    if (layout === "all") drawKundaliChartsPage(doc, reading);
    return null;
  }

  const asc      = reading.chart?.ascendant_english      || "—";
  const chandLag = reading.chart?.chandra?.ascendant_english  || "—";
  const navAsc   = reading.chart?.navamsha?.ascendant_english || "—";
  const usableW  = page.w - margin.x * 2;

  const heading = (text, y, saffron = false) => {
    doc.setFont(fonts.heading, "bold");
    doc.setFontSize(12);
    doc.setTextColor(...hex(saffron ? brand.saffron : brand.ink));
    drawCenteredText(doc, text, y, { charSpace: 1.4 });
    doc.setDrawColor(...hex(brand.gold));
    doc.setLineWidth(0.3);
    doc.line(page.w / 2 - 32, y + 2, page.w / 2 + 32, y + 2);
    return y + 6;
  };

  const caption = (label, value, y) => {
    doc.setFont(fonts.heading, "bold");
    doc.setFontSize(8);
    doc.setTextColor(...hex(brand.gold));
    drawCenteredText(doc, label, y, { charSpace: 1.5 });
    doc.setFont(fonts.body, "bold");
    doc.setFontSize(11);
    doc.setTextColor(...hex(brand.ink));
    setFontForText(doc, value, "bold");
    doc.text(value, page.w / 2, y + 5, { align: "center" });
    return y + 8;
  };

  if (layout === "d1" && d1Snap) {
    // Page 2 layout: D1 chart + caption, followed by planetary positions
    // rendered by the caller below. Chart occupies the top ~110 mm.
    let y = margin.y;
    y = heading(T("kundali_lagna_d1"), y + 2);
    const { h } = drawSnapshot(doc, d1Snap, margin.x, y, usableW, 100);
    y += h + 2;
    caption(T("ascendant_lagna"), asc, y);
    // Return so caller can continue with planetary positions on same page.
    return y + 10;
  }

  if (layout === "chandra+navamsha") {
    // Page 3 layout: Chandra Rashi (top half) + Navamsha D9 (bottom half).
    let y = margin.y;
    if (chSnap) {
      y = heading(T("chandra_rashi_chart"), y + 2);
      const { h } = drawSnapshot(doc, chSnap, margin.x, y, usableW, 95);
      y += h + 2;
      y = caption(T("chandra_lagna"), chandLag, y);
      y += 6;
    }
    if (navSnap) {
      y = heading(T("navamsha_chart_d9"), y + 2);
      const { h } = drawSnapshot(doc, navSnap, margin.x, y, usableW, 95);
      y += h + 2;
      caption(T("navamsha_ascendant"), navAsc, y);
    }
    return;
  }

  // Legacy "all" layout — kept for backward compatibility with ReadingDetail.
  doc.addPage();
  let y = margin.y;
  y = heading("VEDIC KUNDALI CHARTS", y + 2);
  y += 6;
  if (d1Snap) {
    const { h } = drawSnapshot(doc, d1Snap, margin.x, y, usableW, 100);
    y += h + 8;
  }
  const halfW = (usableW - 6) / 2;
  if (chSnap)  drawSnapshot(doc, chSnap,  margin.x,             y, halfW, 90);
  if (navSnap) drawSnapshot(doc, navSnap, margin.x + halfW + 6, y, halfW, 90);
}

/** Snapshot the on-screen Lo Shu Grid + Vedic Planetary Chart into the
 *  Premium Numerology PDF so the coloured cells match what the user sees.
 *  Falls back to the vector tables if the on-screen container isn't found. */
async function drawNumerologyGridsFromScreen(doc, num, y, containerTestId) {
  // Prefer the vector renderers here: they draw Lo Shu and the Vedic
  // Planetary Chart one below the other in generous "expanded" form with
  // native page-break handling. Snapshotting the tall on-screen stack would
  // compress both grids to fit the remaining page height, so vector is a
  // strictly better fit for the PDF layout the user requested.
  // (drawLoShu / drawVedicPlanetChart each render their own section heading.)
  y = drawLoShu(doc, num?.lo_shu, y);
  y = drawVedicPlanetChart(doc, num?.vedic_chart, y);
  void containerTestId; // signature preserved for callers
  return y;
}


/** Dedicated Kundali-Charts page: D1 Lagna centered on top, then Chandra +
 *  Navamsha side-by-side below. Called by both Basic and Premium builders on
 *  a fresh page so the diagrams never share space with tables or reading text. */
function drawKundaliChartsPage(doc, reading) {
  const { page, margin, brand, fonts } = LAYOUT;
  doc.addPage();
  let y = margin.y;

  // Page title
  doc.setFont(fonts.heading, "bold");
  doc.setFontSize(16);
  doc.setTextColor(...hex(brand.ink));
  doc.text("Vedic Kundali Charts", page.w / 2, y, { align: "center" });
  doc.setDrawColor(...hex(brand.gold));
  doc.setLineWidth(0.3);
  doc.line(page.w / 2 - 35, y + 3, page.w / 2 + 35, y + 3);
  y += 12;

  const drawLabelled = (title, chart, cx, cy, sz) => {
    doc.setFont(fonts.heading, "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(...hex(brand.ink));
    doc.text(title, cx + sz / 2, cy - 3, { align: "center" });
    drawKundaliDiagram(doc, chart, cx, cy, sz);
    if (chart?.ascendant_english) {
      doc.setFont(fonts.body, "italic");
      doc.setFontSize(8);
      doc.setTextColor(...hex(brand.subtle));
      doc.text(`Ascendant: ${chart.ascendant_english}`, cx + sz / 2, cy + sz + 5, { align: "center" });
    }
  };

  // D1 Lagna — large, centred
  const d1 = 100;
  drawLabelled("Lagna · D1 · Ascendant Chart", reading.chart, (page.w - d1) / 2, y + 6, d1);
  y += d1 + 22;

  // Chandra + Navamsha side-by-side
  const sm = 76;
  const gap = 12;
  const startX = (page.w - (sm * 2 + gap)) / 2;
  if (reading.chart?.chandra) {
    drawLabelled("Chandra Rashi · Moon-sign Chart", reading.chart.chandra, startX, y + 6, sm);
  }
  if (reading.chart?.navamsha) {
    drawLabelled("Navamsha · D9 · Marriage & Dharma", reading.chart.navamsha, startX + sm + gap, y + 6, sm);
  }
}

// ---------- public builders -------------------------------------------------
// Builders are async because they may await DOM snapshots (html-to-image).
// ResultActions awaits them and calls doc.save() with the returned jsPDF.
//
// Layout matches the reference PDF Vedic-Astrology-Report.pdf (Feb 2026 spec):
//   Page 1 → Cover (SNW brand, Ganesha, name, 3×2 birth grid, Nakshatra summary)
//   Page 2 → KUNDALI LAGNA CHART · D1  +  PLANETARY POSITIONS table
//   Page 3 → CHANDRA RASHI CHART  +  NAVAMSHA CHART · D9
//   Page 4 → VIMSHOTTARI MAHADASHA · 120-YEAR CYCLE
//   Page 5+→ DETAILED VEDIC KUNDALI READING FOR <NAME>  +  AI advice
// The Basic builder uses the same skeleton without the AI-driven detail depth.

export async function buildBasicPdf(reading, inputs) {
  const lang = pickPdfLang(inputs, reading);
  bindLang(lang);
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  await activateScriptFontForLang(doc, lang);
  drawAstroCoverPage(doc, reading, inputs, T("vedic_kundali_report"));

  // Page 2: D1 chart on top, Planetary Positions table below
  doc.addPage();
  let y2 = null;
  if (reading.chart?.houses) {
    y2 = await drawKundaliChartsFromScreen(doc, reading, [
      "basic-expand-kundali-d1",
      "basic-expand-kundali-chandra",
      "basic-expand-kundali-navamsha",
    ], "d1");
  }
  if (y2 == null) y2 = LAYOUT.margin.y;
  drawPlanetaryPositions(doc, reading, y2);

  // Page 3: Chandra Rashi + Navamsha D9
  if (reading.chart?.chandra || reading.chart?.navamsha) {
    doc.addPage();
    await drawKundaliChartsFromScreen(doc, reading, [
      "basic-expand-kundali-d1",
      "basic-expand-kundali-chandra",
      "basic-expand-kundali-navamsha",
    ], "chandra+navamsha");
  }

  // Page 4: Vimshottari Mahadasha
  if (reading.chart?.dasha?.mahadashas?.length) {
    doc.addPage();
    drawVimshottariMahadasha(doc, reading, LAYOUT.margin.y);
  }

  // Page 5+: AI reading
  if (reading.advice) {
    doc.addPage();
    let y = LAYOUT.margin.y;
    const name = (inputs?.full_name || reading.summary?.name || "your reading").toUpperCase();
    y = drawFittedTitle(doc, T("detailed_kundali_reading_for", { name }), y, { size: 14 });
    doc.setDrawColor(...hex(LAYOUT.brand.gold));
    doc.setLineWidth(0.3);
    doc.line(LAYOUT.margin.x + 20, y - 3, LAYOUT.page.w - LAYOUT.margin.x - 20, y - 3);
    y += 6;
    await drawReadingBody(doc, reading.advice, y, "basic-advice", pickPdfLang(inputs, reading));
  }

  drawFooter(doc);
  restoreFonts();
  return doc;
}

export async function buildPremiumAstroPdf(reading, inputs) {
  const lang = pickPdfLang(inputs, reading);
  bindLang(lang);
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  await activateScriptFontForLang(doc, lang);
  drawAstroCoverPage(doc, reading, inputs, T("vedic_kundali_report"));

  // Page 2: D1 Lagna Chart + Planetary Positions
  doc.addPage();
  let y2 = null;
  if (reading.chart?.houses) {
    y2 = await drawKundaliChartsFromScreen(doc, reading, [
      "expand-kundali-d1",
      "expand-kundali-chandra",
      "expand-kundali-navamsha",
    ], "d1");
  }
  if (y2 == null) y2 = LAYOUT.margin.y;
  drawPlanetaryPositions(doc, reading, y2);

  // Page 3: Chandra Rashi + Navamsha D9
  if (reading.chart?.chandra || reading.chart?.navamsha) {
    doc.addPage();
    await drawKundaliChartsFromScreen(doc, reading, [
      "expand-kundali-d1",
      "expand-kundali-chandra",
      "expand-kundali-navamsha",
    ], "chandra+navamsha");
  }

  // Page 4: Vimshottari Mahadasha
  if (reading.chart?.dasha?.mahadashas?.length) {
    doc.addPage();
    drawVimshottariMahadasha(doc, reading, LAYOUT.margin.y);
  }

  // Page 5+: Detailed AI reading
  if (reading.advice) {
    doc.addPage();
    let y = LAYOUT.margin.y;
    const name = (inputs?.full_name || reading.summary?.name || "you").toUpperCase();
    y = drawFittedTitle(doc, T("detailed_kundali_reading_for", { name }), y, { size: 14 });
    doc.setDrawColor(...hex(LAYOUT.brand.gold));
    doc.setLineWidth(0.3);
    doc.line(LAYOUT.margin.x + 20, y - 3, LAYOUT.page.w - LAYOUT.margin.x - 20, y - 3);
    y += 4;
    doc.setFont(LAYOUT.fonts.heading, "bold");
    doc.setFontSize(10);
    doc.setTextColor(...hex(LAYOUT.brand.saffron));
    drawCenteredText(doc, T("detailed_planetary_reading"), y, { charSpace: 1.5 });
    y += 8;
    await drawReadingBody(doc, reading.advice, y, "premium-advice", pickPdfLang(inputs, reading));
  }

  drawFooter(doc);
  restoreFonts();
  return doc;
}

export async function buildPremiumNumerologyPdf(reading, inputs) {
  const lang = pickPdfLang(inputs, reading);
  bindLang(lang);
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  await activateScriptFontForLang(doc, lang);
  drawNumerologyCoverPage(doc, reading, inputs);

  const num = reading.chart?.numerology;

  // Page 2: Numerology Overview (core numbers table)
  doc.addPage();
  let y = LAYOUT.margin.y;
  y = drawNumerologyCore(doc, num, y);

  // Page 3: Lo Shu Grid + Vedic Planetary Chart (raster snapshot of on-screen)
  doc.addPage();
  y = LAYOUT.margin.y;
  y = await drawNumerologyGridsFromScreen(doc, num, y, "premium-numerology-charts");

  // Page 4: Numerology Mahadasha (81-Year Cycle)
  const dasha = reading.chart?.numerology_dasha;
  if (dasha?.mahadashas?.length) {
    doc.addPage();
    y = LAYOUT.margin.y;
    y = drawSectionHeading(doc, T("num_mahadasha_title"), y + 2);
    // Intro copy
    y = drawParagraph(doc, T("num_mahadasha_intro"), y, { size: 9.5, leading: 5 });
    const cur = dasha.current?.mahadasha;
    const rows = dasha.mahadashas.map((m) => [
      `${m.number}${m.number === cur ? `   ${T("current_marker")}` : ""}`,
      m.name || NUMEROLOGY_RULERS[m.number] || "—",
      new Date(m.start).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
      new Date(m.end).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
      String(m.years),
    ]);
    autoTable(doc, {
      startY: y, margin: { left: LAYOUT.margin.x, right: LAYOUT.margin.x },
      head: [[T("col_hash"), T("col_ruler"), T("col_starts"), T("col_ends"), T("col_years")]],
      body: rows, theme: "striped",
      styles: { font: LAYOUT.fonts.body, fontSize: 10, cellPadding: 2, textColor: hex(LAYOUT.brand.body) },
      headStyles: { fillColor: hex(LAYOUT.brand.ink), textColor: [255, 255, 255], fontStyle: "bold", halign: "center" },
      alternateRowStyles: { fillColor: [253, 250, 240] },
      columnStyles: { 0: { halign: "center", fontStyle: "bold" }, 4: { halign: "right" } },
      didParseCell: tableFontHook,
  });
  }

  // Page 5+: Numerology AI reading (only if backend returns one)
  if (reading.numerology_advice) {
    doc.addPage();
    y = LAYOUT.margin.y;
    const name = (inputs?.full_name || reading.summary?.name || "you").toUpperCase();
    y = drawFittedTitle(doc, T("detailed_numerology_reading_for", { name }), y, { size: 14 });
    doc.setDrawColor(...hex(LAYOUT.brand.gold));
    doc.setLineWidth(0.3);
    doc.line(LAYOUT.margin.x + 20, y - 3, LAYOUT.page.w - LAYOUT.margin.x - 20, y - 3);
    y += 6;
    drawMarkdown(doc, reading.numerology_advice, y);
  }

  drawFooter(doc);
  restoreFonts();
  return doc;
}
